import BigNumber from 'bignumber.js'
import erc20ABI from 'config/abi/erc20.json'
import masterchefABI from 'config/abi/ZenMaster.json'
import slaveFarmABI from 'config/abi/slaveFarmABI.json'
import multicall from 'utils/multicall'
import { getAddress, getMasterChefAddress } from 'utils/addressHelpers'
import { getMasterchefContract } from 'utils/contractHelpers'
import { FarmConfig } from 'config/constants/types'

export interface SlaveFarmUserData {
  slaveFarmAddress: string
  stakedBalance: string
  earnings: string  
}

// Function to fetch slave farm user data
export const fetchSlaveFarmUserData = async (pid: number, account: string, slaveFarmAddresses: string[]): Promise<SlaveFarmUserData[]> => {
  const calls = slaveFarmAddresses.flatMap((address) => [
    {
      address,
      name: 'userInfo',
      params: [pid, account],
    },
    {
      address,
      name: 'pendingRewards',
      params: [pid, account],
    },
  ])
  const results = await multicall(slaveFarmABI, calls)

  return slaveFarmAddresses.map((address, index) => {
    const userInfo = results[index * 2]
    const pendingRewards = results[index * 2 + 1]

    return {
      slaveFarmAddress: address,
      stakedBalance: userInfo ? userInfo.amount.toString() : "0",
      earnings: pendingRewards ? pendingRewards.toString() : "0",
    }
  })
}

export const fetchFarmUserAllowances = async (account: string, farmsToFetch: FarmConfig[]) => {
  const masterChefAddress = getMasterChefAddress()

  const calls = farmsToFetch.map((farm) => {
    const lpContractAddress = getAddress(farm.lpAddresses)
    return { address: lpContractAddress, name: 'allowance', params: [account, masterChefAddress] }
  })

  const rawLpAllowances = await multicall(erc20ABI, calls)
  const parsedLpAllowances = rawLpAllowances.map((lpBalance) => {
    return new BigNumber(lpBalance).toJSON()
  })
  return parsedLpAllowances
}

export const fetchFarmUserTokenBalances = async (account: string, farmsToFetch: FarmConfig[]) => {
  const calls = farmsToFetch.map((farm) => {
    const lpContractAddress = getAddress(farm.lpAddresses)
    return {
      address: lpContractAddress,
      name: 'balanceOf',
      params: [account],
    }
  })

  const rawTokenBalances = await multicall(erc20ABI, calls)
  const parsedTokenBalances = rawTokenBalances.map((tokenBalance) => {
    return new BigNumber(tokenBalance).toJSON()
  })
  return parsedTokenBalances
}

export const fetchFarmUserStakedBalances = async (account: string, farmsToFetch: FarmConfig[]) => {
  const masterChefAddress = getMasterChefAddress()

  const calls = farmsToFetch.map((farm) => {
    return {
      address: masterChefAddress,
      name: 'userInfo',
      params: [farm.pid, account],
    }
  })

  const rawStakedBalances = await multicall(masterchefABI, calls)
  const parsedStakedBalances = rawStakedBalances.map((stakedBalance) => {
    return new BigNumber(stakedBalance[0]._hex).toJSON()
  })
  return parsedStakedBalances
}

export const fetchFarmUserEarnings = async (account: string, farmsToFetch: FarmConfig[]) => {
  const masterChefAddress = getMasterChefAddress()
  const calls = farmsToFetch.map((farm) => {
    return {
      address: masterChefAddress,
      name: 'pendingRewards',
      params: [farm.pid, account],
    }

  })
  
  const rawEarnings = await multicall(masterchefABI, calls)
  const parsedEarnings = rawEarnings.map((earnings) => {
    return new BigNumber(earnings).toJSON()
  })
  return parsedEarnings
}
